import * as React from 'react';
import { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { IconComponentType } from '../icon-button';
import { cn } from '@restworld/utils-common';
import {
  ArrowIconWrapper,
  StyledSelectHelperText,
  HelperTextVariantType,
  ListboxButtonContent,
  ListboxContentWrapper,
  SelectMainWrapper,
  StyledPlaceholder,
  SecondaryLabel,
  selectLabelStyle,
  selectInteractiveAreaStyle,
  selectOptionsStyle,
  selectOptionStyle,
} from './select.styled';

export function Select({
  disabled,
  label,
  secondaryLabel,
  value,
  onChange,
  options,
  fullwidth,
  wrapperStyle,
  helperText: { variant: helperTextVariant, message: helperTextMessage } = { variant: 'error', message: '' },
  placeholder = '',
  tertiaryLabel = '',
}: SelectProps) {
  const placeholderEl = useMemo(() => <StyledPlaceholder>{placeholder}</StyledPlaceholder>, [placeholder]);

  return (
    <SelectMainWrapper fullwidth={fullwidth} twStyle={wrapperStyle}>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <ListboxContentWrapper>
              {label && (
                <StyledListboxLabel disabled={disabled}>
                  <span>{label}</span> <SecondaryLabel>{secondaryLabel}</SecondaryLabel>
                </StyledListboxLabel>
              )}
              <StyledListboxButton aria-disabled={disabled} disabled={disabled}>
                <ListboxButtonContent>
                  {tertiaryLabel ? <span>{tertiaryLabel}</span> : null}
                  {value ? value.label : placeholderEl}
                </ListboxButtonContent>
                <ArrowIconWrapper isOpen={open}>
                  <ChevronDownIcon className="h-6 w-6 text-neutral-700" aria-hidden="true" />
                </ArrowIconWrapper>
              </StyledListboxButton>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <StyledListboxOptions>
                  {options?.map((option, index) => {
                    const { Icon, label } = option;

                    return (
                      <Listbox.Option key={index} value={option}>
                        {({ active }) => (
                          <StyledListboxOption active={active}>
                            {Icon ? <Icon className="text-neutral-700 h-6 w-6" /> : null}
                            <span>{label}</span>
                          </StyledListboxOption>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </StyledListboxOptions>
              </Transition>
            </ListboxContentWrapper>
            {helperTextMessage && (
              <StyledSelectHelperText variant={helperTextVariant}>{helperTextMessage}</StyledSelectHelperText>
            )}
          </>
        )}
      </Listbox>
    </SelectMainWrapper>
  );
}
export default Select;
export interface SelectProps {
  placeholder?: string;
  disabled?: boolean;
  helperText?: { variant?: HelperTextVariantType; message: string };
  label?: string;
  secondaryLabel?: string;
  value?: SelectOptionType;
  onChange?: (value: SelectOptionType) => void;
  options?: SelectOptionType[];
  fullwidth?: boolean;
  wrapperStyle?: string;
  tertiaryLabel?: string;
}
export type SelectOptionType = { value?: string; Icon?: IconComponentType; label?: string; key?: string };

const StyledListboxLabel = ({ disabled, className, ...props }: any) => (
  <Listbox.Label className={cn(selectLabelStyle, disabled && 'text-neutral-100', className)} {...props} />
);
const StyledListboxButton = ({ disabled, className, ...props }: any) => (
  <Listbox.Button
    className={cn(
      selectInteractiveAreaStyle,
      disabled && 'bg-neutral-40 hover:bg-neutral-40 cursor-not-allowed',
      className
    )}
    {...props}
  />
);
const StyledListboxOptions = React.forwardRef<HTMLUListElement, any>(({ className, ...props }, ref) => (
  <Listbox.Options ref={ref} className={cn(selectOptionsStyle, className)} {...props} />
));
StyledListboxOptions.displayName = 'StyledListboxOptions';
const StyledListboxOption = ({ active, className, ...props }: any) => (
  <span
    className={cn(
      selectOptionStyle,
      'flex items-center gap-2',
      active ? 'bg-neutral-30 rounded-lg w-full inline-block h-12 flex items-center' : '',
      className
    )}
    {...props}
  />
);
